import React, { useState } from 'react';
import type { CategorySchema } from '../types/healthUpdate';
import { UPDATABLE_CATEGORIES } from '../types/healthUpdate';
import { submitHealthUpdate } from '../services/healthUpdateService';
import { useAuth } from '../context/AuthContext';
import './PatientUpdateModal.css';

interface PatientUpdateModalProps {
  onClose: () => void;
  onSubmitted: () => void;
}

const PatientUpdateModal: React.FC<PatientUpdateModalProps> = ({ onClose, onSubmitted }) => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<CategorySchema | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [ackChecked, setAckChecked] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const expectedName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const nameMatches = expectedName
    ? signedName.trim().toLowerCase() === expectedName.toLowerCase()
    : signedName.trim().length >= 2;
  const showNameError = signedName.trim().length > 0 && !nameMatches && !!expectedName;

  const handleCategoryChange = (key: string) => {
    const cat = UPDATABLE_CATEGORIES.find(c => c.key === key) || null;
    setSelectedCategory(cat);
    if (cat) {
      const initial: Record<string, string> = {};
      cat.fields.forEach(f => { initial[f.key] = ''; });
      setFormData(initial);
    } else {
      setFormData({});
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const isValidDate = (value: string): boolean => {
    if (!value) return true; // empty is OK (not-required or optional)
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return false;
    const d = new Date(value + 'T00:00:00');
    return !isNaN(d.getTime())
      && d.getFullYear() === Number(match[1])
      && d.getMonth() + 1 === Number(match[2])
      && d.getDate() === Number(match[3]);
  };

  const dateFieldsValid = selectedCategory
    ? selectedCategory.fields
        .filter(f => f.type === 'date')
        .every(f => isValidDate(formData[f.key] || ''))
    : true;

  const hasRequiredFields = selectedCategory
    ? selectedCategory.fields.filter(f => f.required).every(f => formData[f.key]?.trim())
    : false;

  const canSubmit = selectedCategory && hasRequiredFields && dateFieldsValid && ackChecked && nameMatches && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !selectedCategory) return;

    setSubmitting(true);
    setError('');

    try {
      await submitHealthUpdate(
        selectedCategory.key,
        formData,
        { confirmed: true, signedName: signedName.trim() }
      );
      onSubmitted();
    } catch (err: any) {
      setError(err.message || 'Failed to submit update');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pu-modal-overlay" onClick={onClose}>
      <div className="pu-modal" onClick={e => e.stopPropagation()}>
        <div className="pu-modal-header">
          <h2>Request a Change</h2>
          <button className="pu-close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="pu-modal-body">
          <div className="pu-fields">
            <div className="pu-field">
              <label className="pu-label">
                Category
                <span className="pu-required">*</span>
              </label>
              <select
                className="pu-input pu-select"
                value={selectedCategory?.key || ''}
                onChange={e => handleCategoryChange(e.target.value)}
              >
                <option value="">Select a category...</option>
                {UPDATABLE_CATEGORIES.map(cat => (
                  <option key={cat.key} value={cat.key}>{cat.label}</option>
                ))}
              </select>
            </div>

            {selectedCategory && selectedCategory.fields.map(field => {
              const value = formData[field.key] || '';
              const isDate = field.type === 'date';
              const showDateError = isDate && value.length > 0 && !isValidDate(value);
              return (
                <div key={field.key} className="pu-field">
                  <label className="pu-label">
                    {field.label}
                    {field.required && <span className="pu-required">*</span>}
                  </label>
                  <input
                    type={isDate ? 'date' : 'text'}
                    className={`pu-input${showDateError ? ' pu-input-error' : ''}`}
                    value={value}
                    onChange={e => handleFieldChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    autoComplete="off"
                  />
                  {showDateError && (
                    <span className="pu-field-error">Please enter a valid date</span>
                  )}
                </div>
              );
            })}
          </div>

          {selectedCategory && (
            <>
              <div className="pu-acknowledgement">
                <h3 className="pu-ack-title">Acknowledgement</h3>
                <label className="pu-checkbox-label">
                  <input
                    type="checkbox"
                    checked={ackChecked}
                    onChange={e => setAckChecked(e.target.checked)}
                    className="pu-checkbox"
                  />
                  <span>I confirm this information is accurate to the best of my knowledge</span>
                </label>
                <div className="pu-field">
                  <label className="pu-label">
                    Type your full name as digital signature
                    <span className="pu-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`pu-input${showNameError ? ' pu-input-error' : ''}`}
                    value={signedName}
                    onChange={e => setSignedName(e.target.value)}
                    placeholder={expectedName || 'Your full name'}
                    autoComplete="off"
                  />
                  {showNameError && (
                    <span className="pu-name-error">
                      Name must match your profile name: {expectedName}
                    </span>
                  )}
                </div>
              </div>

              {error && <div className="pu-error">{error}</div>}
            </>
          )}

          <div className="pu-actions">
            <button type="button" className="pu-btn pu-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="pu-btn pu-btn-submit" disabled={!canSubmit}>
              {submitting ? 'Submitting...' : 'Submit Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientUpdateModal;
